import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInMilliseconds,
  format,
  isWeekend,
  parseISO,
  set,
  startOfDay,
  subDays,
} from "date-fns";

import type { PersonalTodo, TodoRecurrence, TodoReminder } from "../types";

/** Hour of the day used by the "morning" reminder presets. */
export const MORNING_REMINDER_HOUR = 9;

export const TODO_REMINDERS: TodoReminder[] = [
  "none",
  "day_start",
  "end_of_day_1h",
  "end_of_day_3h",
  "day_before",
  "week_before",
  "custom",
];

export const TODO_RECURRENCES: TodoRecurrence[] = [
  "none",
  "daily",
  "weekdays",
  "weekly",
  "monthly",
  "yearly",
  "custom",
];

/** Formats a local date as `YYYY-MM-DD`, the format stored in `due_date`. */
export const toDateOnly = (date: Date): string => format(date, "yyyy-MM-dd");

/** Parses a `YYYY-MM-DD` string as local midnight. */
export const parseDateOnly = (value: string): Date => parseISO(value);

const atHour = (day: Date, hours: number): Date =>
  set(day, { hours, minutes: 0, seconds: 0, milliseconds: 0 });

/**
 * Computes the absolute reminder instant (ISO string) for a to-do whose deadline
 * is the end of `dueDate` in the user's local time zone.
 */
export function computeRemindAt(
  dueDate: string,
  reminder: TodoReminder,
  customRemindAt?: string | null,
): string | null {
  const day = parseDateOnly(dueDate);
  switch (reminder) {
    case "none":
      return null;
    case "day_start":
      return atHour(day, MORNING_REMINDER_HOUR).toISOString();
    case "end_of_day_1h":
      return atHour(day, 23).toISOString();
    case "end_of_day_3h":
      return atHour(day, 21).toISOString();
    case "day_before":
      return atHour(subDays(day, 1), MORNING_REMINDER_HOUR).toISOString();
    case "week_before":
      return atHour(subDays(day, 7), MORNING_REMINDER_HOUR).toISOString();
    case "custom":
      return customRemindAt ? new Date(customRemindAt).toISOString() : null;
  }
}

const stepDueDate = (
  day: Date,
  recurrence: Exclude<TodoRecurrence, "none">,
  interval: number,
): Date => {
  switch (recurrence) {
    case "daily":
      return addDays(day, 1);
    case "weekdays": {
      let next = addDays(day, 1);
      while (isWeekend(next)) {
        next = addDays(next, 1);
      }
      return next;
    }
    case "weekly":
      return addWeeks(day, 1);
    case "monthly":
      return addMonths(day, 1);
    case "yearly":
      return addYears(day, 1);
    case "custom":
      return addDays(day, interval);
  }
};

/**
 * Returns the due date of the next occurrence of a recurring to-do, always
 * strictly after `today`, so completing an overdue item never schedules
 * another overdue one. Returns null for non-recurring to-dos.
 */
export function getNextDueDate(
  dueDate: string,
  recurrence: TodoRecurrence,
  interval: number | null | undefined,
  today: Date = new Date(),
): string | null {
  if (recurrence === "none") {
    return null;
  }
  const step = Math.max(1, interval ?? 1);
  const todayStart = startOfDay(today);
  let next = stepDueDate(parseDateOnly(dueDate), recurrence, step);
  while (next <= todayStart) {
    next = stepDueDate(next, recurrence, step);
  }
  return toDateOnly(next);
}

/**
 * Builds the next occurrence of a recurring to-do. A custom reminder keeps the
 * same offset from the deadline as the completed occurrence.
 */
export function buildNextOccurrence(
  todo: PersonalTodo,
  today: Date = new Date(),
): Omit<PersonalTodo, "id"> | null {
  const nextDueDate = getNextDueDate(
    todo.due_date,
    todo.recurrence,
    todo.recurrence_interval,
    today,
  );
  if (!nextDueDate) {
    return null;
  }

  let remindAt = computeRemindAt(nextDueDate, todo.reminder);
  if (todo.reminder === "custom" && todo.remind_at) {
    const offset = differenceInMilliseconds(
      new Date(todo.remind_at),
      parseDateOnly(todo.due_date),
    );
    remindAt = new Date(
      parseDateOnly(nextDueDate).getTime() + offset,
    ).toISOString();
  }

  return {
    title: todo.title,
    description: todo.description ?? null,
    due_date: nextDueDate,
    reminder: todo.reminder,
    remind_at: remindAt,
    reminded_at: null,
    recurrence: todo.recurrence,
    recurrence_interval: todo.recurrence_interval ?? null,
    done_at: null,
    sales_id: todo.sales_id,
  };
}

export type TodoGroup = "overdue" | "today" | "upcoming";

export const getTodoGroup = (
  dueDate: string,
  today: Date = new Date(),
): TodoGroup => {
  const todayValue = toDateOnly(today);
  if (dueDate < todayValue) return "overdue";
  if (dueDate === todayValue) return "today";
  return "upcoming";
};
